import React, { useState } from 'react';
import {
  AppBar,
  Box,
  Button,
  Checkbox,
  Chip,
  Container,
  FormControlLabel,
  Grid,
  LinearProgress,
  Paper,
  Stack,
  Stepper,
  Step,
  StepLabel,
  Toolbar,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { styled } from '@mui/system';
import SitemarkIcon from '../../components/SitemarkIcon';
import { listaModulos } from '../../utils/staticvariables';

const Header = styled(AppBar)({
  backgroundColor: 'white',
  boxShadow: 'none',
  borderBottom: '1px solid #eaedf1',
  padding: '12px 40px',
});

const ModuleCard = styled(Paper, {
  shouldForwardProp: (prop) => prop !== 'selected' && prop !== 'recommended',
})(({ selected, recommended }) => ({
  padding: '16px 18px',
  borderRadius: 12,
  border: `1.5px solid ${selected ? '#3f6ed8' : '#e7edf4'}`,
  backgroundColor: selected ? '#f4f8ff' : 'white',
  cursor: 'pointer',
  transition: 'all 150ms ease',
  boxShadow: selected ? '0 2px 8px rgba(63, 110, 216, 0.12)' : '0 1px 2px rgba(16, 20, 24, 0.03)',
  '&:hover': {
    borderColor: '#3f6ed8',
    backgroundColor: selected ? '#f4f8ff' : '#fafbfd',
  },
  position: 'relative',
}));

const Step2moduleselection = ({ form, setForm, onBack, onNext }) => {

  const computeInitialModules = () => {
    // Si ya vienen seleccionados, restáuralos
    if (form.selectedModules) {
      return listaModulos.reduce((acc, group) => {
        group.modulos.forEach((mod) => {
          acc[mod.key] = form.selectedModules.includes(mod.key);
        });
        return acc;
      }, {});
    }

    // Si es la primera vez, calcular por companyTypes
    return listaModulos.reduce((acc, group) => {
      group.modulos.forEach((mod) => {
        if (form.general.companyTypes && form.general.companyTypes.length > 0 && form.general.companyTypes.some(type => mod.companytypes.includes(type))) {
          acc[mod.key] = true; // Si es un módulo por defecto, lo seleccionamos
        }
        else {
          acc[mod.key] = mod.default; // Si no, lo dejamos como estaba
        }


      });
      return acc;
    }, {});
  };

  const [modules, setModules] = useState(computeInitialModules());

  const handleChange = (key) => (event) => {
    setModules({ ...modules, [key]: event.target.checked });
  };

  const handleNext = () => {

    const selectedModules = Object.keys(modules).filter((key) => modules[key]);
    setForm({ ...form, selectedModules });
    onNext();
  }

  const handleBack = () => {
    const selectedModules = Object.keys(modules).filter((key) => modules[key]);
    setForm({ ...form, selectedModules });
    onBack();
  }

  const totalSelected = Object.values(modules).filter(Boolean).length;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: '#f8fafc',
        fontFamily: 'Inter, Noto Sans, sans-serif',
      }}
    >
      <Header position="sticky">
        <Toolbar sx={{ justifyContent: 'space-between', px: '0 !important' }}>
          <Box display="flex" alignItems="center" gap={2}>
            <Box width={28} height={28}>
              <SitemarkIcon />
            </Box>
          </Box>
          <Typography variant="body2" color="text.secondary">
            Paso 2 de 3
          </Typography>
        </Toolbar>
      </Header>

      <Container maxWidth="md" sx={{ py: 6 }}>
        <Box sx={{ mb: 5 }}>
          <Stepper activeStep={1} alternativeLabel>
            <Step><StepLabel>Tu empresa</StepLabel></Step>
            <Step><StepLabel>Módulos</StepLabel></Step>
            <Step><StepLabel>Revisar</StepLabel></Step>
          </Stepper>
        </Box>

        <Box textAlign="center" mb={5}>
          <Typography variant="h4" fontWeight={700} color="#101418" gutterBottom>
            Elige los módulos que usarás
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 540, mx: 'auto' }}>
            Hemos marcado los recomendados según el tipo de financiera seleccionado. Puedes activar o desactivar cualquiera.
          </Typography>
          <Chip
            label={`${totalSelected} seleccionados`}
            size="small"
            sx={{ mt: 2, bgcolor: '#eff4fb', color: '#3f6ed8', fontWeight: 600 }}
          />
        </Box>

        {modules != undefined && listaModulos.map((grupo) => (
          <Box key={grupo.categoria} mb={4}>
            <Typography variant="overline" fontWeight={700} color="text.secondary" letterSpacing={1}>
              {grupo.categoria}
            </Typography>
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              {grupo.modulos.map((mod) => {
                const isSelected = !!modules[mod.key];
                const isRecommended = form.general.companyTypes && form.general.companyTypes.length > 0 && form.general.companyTypes.some(type => mod.companytypes.includes(type));
                const isLocked = mod.default;

                return (
                  <Grid item xs={12} sm={6} md={4} key={mod.key}>
                    <ModuleCard
                      elevation={0}
                      selected={isSelected}
                      recommended={isRecommended}
                      onClick={() => {
                        if (!isLocked) handleChange(mod.key)({ target: { checked: !isSelected } });
                      }}
                      sx={{ cursor: isLocked ? 'not-allowed' : 'pointer', opacity: isLocked ? 0.85 : 1 }}
                    >
                      <Stack direction="row" spacing={1.5} alignItems="flex-start">
                        <Checkbox
                          disabled={isLocked}
                          checked={isSelected}
                          onChange={handleChange(mod.key)}
                          onClick={(e) => e.stopPropagation()}
                          sx={{ p: 0, mt: '2px' }}
                        />
                        <Box flex={1}>
                          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                            <Typography fontWeight={600} color="#101418" fontSize="0.95rem">
                              {mod.label}
                            </Typography>
                            {isRecommended && !isLocked && (
                              <Chip
                                icon={<CheckCircleIcon sx={{ fontSize: 14 }} />}
                                label="Recomendado"
                                size="small"
                                sx={{ height: 20, fontSize: '0.7rem', bgcolor: '#e7f4ec', color: '#2e7d4d', '& .MuiChip-icon': { color: '#2e7d4d', ml: '6px' } }}
                              />
                            )}
                            {isLocked && (
                              <Chip
                                label="Incluido"
                                size="small"
                                sx={{ height: 20, fontSize: '0.7rem', bgcolor: '#eff4fb', color: '#3f6ed8' }}
                              />
                            )}
                          </Stack>
                        </Box>
                      </Stack>
                    </ModuleCard>
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        ))}

        <Box mt={4}>
          <LinearProgress variant="determinate" value={66} sx={{ bgcolor: '#e7edf4', height: 6, borderRadius: 1 }} />
        </Box>

        <Box display="flex" justifyContent="space-between" mt={3}>
          <Button
            variant="outlined"
            size="large"
            startIcon={<ArrowBackIcon />}
            onClick={() => handleBack()}
            sx={{ fontWeight: 600, px: 3, py: 1.2, borderRadius: 2, textTransform: 'none' }}
          >
            Anterior
          </Button>

          <Button
            variant="contained"
            size="large"
            endIcon={<ArrowForwardIcon />}
            onClick={() => handleNext()}
            sx={{ fontWeight: 600, px: 4, py: 1.2, borderRadius: 2, textTransform: 'none' }}
          >
            Continuar
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default Step2moduleselection;
